import {
  useActiveLab,
  useOrderProductsFromOrderId,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Card from '@/components/Card';
import OrderProductsList from '@/components/OrderProductsList';
import OrderProductDialog from '@/dialogs/OrderProductDialog';
import Section from '../../Section';
import SectionTitle from '../../SectionTitle';
import EmptyMessage from '../../EmptyMessage';
import ProductsToggle from './ProductsToggle';
import { classes, filterProducts } from './utils';

export type ProductsSectionProps = {
  isFetchingData: boolean;
};

export default function ProductsSection({
  isFetchingData,
}: ProductsSectionProps) {
  const { activeOrderId } = useAppState();
  const activeLab = useActiveLab();
  const orderProducts = useOrderProductsFromOrderId(activeOrderId);
  const [showAll, toggleShowAll] = useToggle(true);
  const [isAddingNew, toggleAddingNew] = useToggle();

  const renderProducts = () => {
    if (isEmpty(orderProducts)) {
      return <EmptyMessage>No products for current order.</EmptyMessage>;
    }
    let filteredProducts = filterProducts(orderProducts, showAll);
    if (isEmpty(filteredProducts)) {
      return <EmptyMessage>No products matching current filters.</EmptyMessage>;
    }
    return (
      <Card className={classes.card}>
        <OrderProductsList
          isFetchingData={isFetchingData}
          className={classes.list}
          orderProducts={filteredProducts}
        />
      </Card>
    );
  };

  if (isFetchingData || !activeOrderId) return null;

  return (
    <>
      {isAddingNew && activeLab && (
        <OrderProductDialog
          orderId={activeOrderId}
          onClose={() => toggleAddingNew(false)}
        />
      )}
      <Section>
        {activeLab ? (
          <SectionTitle
            title="Products"
            info="Products & statuses can only be changed by lab members"
            newLabel="Add product"
            onAddNew={() => toggleAddingNew(true)}
            renderControls={() => (
              <ProductsToggle
                className={classes.toggle}
                optionClassName={classes.toggleOption}
                value={showAll}
                onChange={toggleShowAll}
              />
            )}
          />
        ) : (
          <SectionTitle title="Products" />
        )}
        {renderProducts()}
      </Section>
    </>
  );
}
